import {
  BrokenImageRounded,
  CallMissedOutgoingRounded,
  HourglassBottomRounded,
  InsertDriveFileOutlined,
  PhotoCamera,
  QuizOutlined,
  SlideshowRounded,
} from "@mui/icons-material";
import { Box, SxProps } from "@mui/material";
import VIDEO_TYPE from "enums/VIDEO_TYPE";
import useGetSignedVideoUrls from "hooks/api/useGetSignedVideoUrls";
import useGetVideoById from "hooks/api/useGetVideoById";
import useGetBlurredProjects from "hooks/useGetBlurredProjects";
import { useState } from "react";
import colors from "theme/base/colors";
import { FILE_TYPE } from "types/VideoDto";
import { flexCentered } from "../theme/base/flex";

type Props = { sx?: SxProps } & (
  | {
      videoId: string;
      thumbnailUrl?: never;
    }
  | {
      videoId?: never;
      thumbnailUrl: string;
    }
);

export default function VideoThumbnail({ sx, thumbnailUrl, videoId }: Props) {
  const [hasError, setHasError] = useState(false);
  const { video } = useGetVideoById(videoId, {
    suspendRequest: !videoId,
  });

  const { thumbnail: signedThumbnail, original: signedOriginal } =
    useGetSignedVideoUrls(video?.id, {
      suspendRequest: !video?.id,
    });
  const blur = useGetBlurredProjects();

  const src =
    thumbnailUrl ||
    signedThumbnail ||
    (video?.type === FILE_TYPE.IMAGE ? signedOriginal : undefined);

  const iconSx: SxProps = {
    height: "100%",
    width: "100%",
    backgroundColor: "gray",
    color: colors.white.main,
    p: "0.5rem",
  };

  let Icon = SlideshowRounded;
  if (hasError) {
    Icon = BrokenImageRounded;
  } else if (video?.inProgress) {
    Icon = HourglassBottomRounded;
  } else if (video?.type == VIDEO_TYPE.DATA) {
    Icon = QuizOutlined;
    iconSx.backgroundColor = colors.grey[600];
  } else if (video?.type === FILE_TYPE.IMAGE) {
    Icon = PhotoCamera;
    iconSx.backgroundColor = colors.grey[700];
  } else if (video?.type == VIDEO_TYPE.FILE || video?.type == VIDEO_TYPE.PDF) {
    Icon = InsertDriveFileOutlined;
    iconSx.backgroundColor = colors.grey[700];
  } else if (video?.skipped) {
    Icon = CallMissedOutgoingRounded;
  }

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "100%",
        color: colors.text.secondary,
        ...flexCentered,
        ...(blur && { filter: "blur(6px)", pointerEvents: "none" }),
        ...sx,
        display: "block",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      {!hasError && src ? (
        <img
          src={src}
          alt="thumb"
          loading="lazy"
          onError={() => setHasError(true)}
          style={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            display: "block",
          }}
        />
      ) : (
        <Icon sx={iconSx} />
      )}
    </Box>
  );
}
